import { onDeepLink } from "src/lib/runtime/deepLinks";
import { currentWindowKind } from "src/lib/runtime/environment";

/**
 * Universal links: macOS hands the desktop app an https://app.fraction.com URL (clicked in Slack,
 * Mail, Safari…) when its path is claimed by apple-app-site-association.json (at the package root;
 * `bun run aasa:publish` uploads it). Keep that file's paths and this origin in step.
 */
const APP_LINK_ORIGIN = "https://app.fraction.com";
const LAST_HANDLED_LINK_KEY = "appLinks.lastHandledLink";

/** The in-app route an app.fraction.com link points at; undefined for any other URL. */
export const parseAppLink = (url: string): string | undefined => {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return undefined;
  }
  if (parsed.origin !== APP_LINK_ORIGIN) return undefined;
  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
};

/**
 * The plugin replays the latest link — launch or not — after every reload. Remember the last one
 * acted on so a reload stays where the user navigated since.
 */
const readLastHandledLink = (): string | null => {
  try {
    return sessionStorage.getItem(LAST_HANDLED_LINK_KEY);
  } catch {
    return null;
  }
};

const rememberHandledLink = (url: string) => {
  try {
    sessionStorage.setItem(LAST_HANDLED_LINK_KEY, url);
  } catch {
    // Without storage a reload may re-open the link; that beats dropping it.
  }
};

const focusMainWindow = async () => {
  const { getCurrentWindow } = await import("@tauri-apps/api/window");
  const mainWindow = getCurrentWindow();
  try {
    await mainWindow.show();
    await mainWindow.unminimize();
    await mainWindow.setFocus();
  } catch (error) {
    console.warn("Opened an app link, but the main window could not be focused.", error);
  }
};

/** Routes app.fraction.com links into the main window. Only the main window listens. */
export const installAppLinkNavigation = (navigate: (path: string) => unknown) => {
  if (currentWindowKind() !== "main") return;

  void onDeepLink((urls, source) => {
    // A batch can carry several links; the last one is where the user meant to end up.
    const url = urls.findLast((candidate) => parseAppLink(candidate) !== undefined);
    const path = url && parseAppLink(url);
    if (!url || !path) return;
    if (source === "launch" && readLastHandledLink() === url) return;
    rememberHandledLink(url);

    void navigate(path);
    if (source === "open") void focusMainWindow();
  });
};
